import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { TranslatorService } from "../../../../core/translator/translator.service";
import { StorageService } from "../../../services/storage.service";
import { Uris } from "../../../services/Uris";
import {
  HydroDevice,
  HydroDeviceBody,
  PaginatedHydroDevices
} from "../interfaces/device.interface";

@Injectable({
  providedIn: "root"
})
export class HydroDevicesService {
  constructor(
    private http: HttpClient,
    public storageService: StorageService,
    public translatorService: TranslatorService
  ) {}

  /**
   * Obtiene la lista paginada de hidrófonos.
   * @param query Cadena de query params, ej: `?page=1&page_size=30&name=v1`.
   * @returns Observable con la respuesta paginada.
   */
  list(query = ""): Observable<PaginatedHydroDevices> {
    return this.http.get<PaginatedHydroDevices>(
      `${Uris.HYDRO_DEVICES}${query}`,
      this.storageService.headers()
    );
  }

  /**
   * Obtiene un hidrófono por su id.
   * @param id Identificador del hidrófono.
   * @returns Observable con el hidrófono.
   */
  get(id: number): Observable<HydroDevice> {
    return this.http.get<HydroDevice>(`${Uris.HYDRO_DEVICES}${id}/`, this.storageService.headers());
  }

  /**
   * Crea un hidrófono.
   * @param body Datos del hidrófono.
   * @returns Observable con el hidrófono creado.
   */
  create(body: HydroDeviceBody): Observable<HydroDevice> {
    return this.http
      .post<HydroDevice>(`${Uris.HYDRO_DEVICES}`, body, this.storageService.headers())
      .pipe(
        map((resp) => {
          this.showSuccess("admin.hydros.management.created", resp.name);
          return resp;
        })
      );
  }

  /**
   * Edita parcialmente un hidrófono.
   * @param id Identificador del hidrófono.
   * @param body Campos a actualizar.
   * @returns Observable con el hidrófono actualizado.
   */
  update(id: number, body: Partial<HydroDeviceBody>): Observable<HydroDevice> {
    return this.http
      .patch<HydroDevice>(`${Uris.HYDRO_DEVICES}${id}/`, body, this.storageService.headers())
      .pipe(
        map((resp) => {
          this.showSuccess("admin.hydros.management.updated", resp.name);
          return resp;
        })
      );
  }

  /**
   * Elimina un hidrófono.
   * @param id Identificador del hidrófono.
   * @returns Observable que completa al eliminar.
   */
  delete(id: number): Observable<void> {
    return this.http
      .delete<void>(`${Uris.HYDRO_DEVICES}${id}/`, this.storageService.headers())
      .pipe(
        map((resp) => {
          this.showSuccess("admin.hydros.management.deleted");
          return resp;
        })
      );
  }

  /**
   * Muestra el toast de exito de una operacion.
   * @param key Clave i18n del mensaje.
   * @param name Nombre del hidrófono para interpolar.
   */
  private showSuccess(key: string, name = ""): void {
    this.storageService.animation(
      "success",
      this.translatorService.translate("admin.hydros.management.success-title"),
      this.translatorService.translate(key, { value: name })
    );
  }
}
